import {
  PublicClientApplication,
  AuthenticationResult,
  InteractionRequiredAuthError,
  AccountInfo,
} from "@azure/msal-browser";
import { msalConfig } from "../authConfig";
import axios from "axios";
// import { useDataContext } from "../components/DataContext/PortfolioDataContext";
// import { datadogRum } from "@datadog/browser-rum";
const msalInstance = new PublicClientApplication(msalConfig);


// Function to initialize MSAL (only called once)
const initializeMSAL = async () => {
  try {
    await msalInstance.initialize();
    const response = await msalInstance.handleRedirectPromise();

    if (response) {
      msalInstance.setActiveAccount(response.account);
    }

    const accounts = msalInstance.getAllAccounts();
    if (accounts.length > 0) {
      msalInstance.setActiveAccount(accounts[0]);
      return true;
    }

    return false;
  } catch (error) {
    console.error("Failed to initialize MSAL:", error);
    return false;
  }
};

// Function to sign in
export const signIn = async (): Promise<AuthenticationResult | null> => {
  try {
    const loginResponse = await msalInstance.loginPopup({
      scopes: ["openid", "profile", "email", "User.Read","Group.Read.All"],
    });

    msalInstance.setActiveAccount(loginResponse.account);
    console.log("Login successful:", loginResponse);

    // Cache user email
    getUserInfo(true);

    return loginResponse;
  } catch (error) {
    console.error("Login failed:", error);
    return null;
  }
};

let tokenCheckInterval: ReturnType<typeof setInterval> | null = null;

export const startTokenPolling = () => {
  stopTokenPolling(); 
  tokenCheckInterval = setInterval(async () => {
    const token = await getAuthToken();
    if (!token) {
      console.warn("Token expired or session invalid. Logging out...");
      await msalInstance.logoutRedirect({ postLogoutRedirectUri: "/" });
    }
  }, 10 * 60 * 1000);
};

export const stopTokenPolling = () => {
  if (tokenCheckInterval) {
    clearInterval(tokenCheckInterval);
    tokenCheckInterval = null;
  }
};

const clearStorageAndRedirect = async () => {
  try {
    localStorage.clear();
    sessionStorage.clear();

    // Clear custom IndexedDB (cacheDB)
    try {
      const { cacheDB } = await import("../components/CacheDB/cacheDB");
      if (cacheDB?.responses?.clear) {
        await cacheDB.responses.clear();
        console.log("Custom cacheDB cleared.");
      }
    } catch (e) {
      console.warn("Failed to clear cacheDB:", e);
    }

    // Clear all browser IndexedDB databases
    if (indexedDB?.databases) {
      const databases = await indexedDB.databases();
      for (const db of databases) {
        if (db.name) {
          indexedDB.deleteDatabase(db.name);
        }
      }
    } else {
      console.warn("indexedDB.databases() not supported.");
    }

    // Allow async ops to settle before redirect
    setTimeout(() => {
      window.location.href = "/";
    }, 100);
  } catch (error) {
    console.error("Error during cleanup:", error);
    window.location.href = "/";
  }
};

// Function to get the auth token
export const getAuthToken = async (scope?: string): Promise<string | null> => {
  const resolvedScope = (scope ?? process.env.REACT_APP_API_SCOPE ?? "").trim();

  try {
    if (!resolvedScope) {
      throw new Error("Missing API scope configuration for token acquisition.");
    }

    let account: AccountInfo | null = msalInstance.getActiveAccount();

    // Try to get account if not already active
    if (!account) {
      const accounts = msalInstance.getAllAccounts();
      if (accounts.length > 0) {
        account = accounts[0];
        msalInstance.setActiveAccount(account);
      } else {
        console.warn("No account found. Redirecting...");
        await clearStorageAndRedirect();
        return null;
      }
    }

    // Try to get access token silently
    const response = await msalInstance.acquireTokenSilent({
      scopes: [resolvedScope],
      account,
    });

    return response.accessToken;
  } catch (error: any) {
    console.error("Token acquisition failed:", error);

    if (error instanceof InteractionRequiredAuthError || error.errorCode === "interaction_required") {
      console.warn("Token requires interaction — attempting popup...");
      try {
        const account = msalInstance.getActiveAccount() || msalInstance.getAllAccounts()[0];
        const popupResponse = await msalInstance.acquireTokenPopup({
          scopes: [resolvedScope],
          account,
        });
        return popupResponse.accessToken;
      } catch (popupError) {
        console.error("Popup token acquisition also failed:", popupError);
      }
    } else {
      console.warn("Unexpected token error. Redirecting...");
    }

    stopTokenPolling();
    await clearStorageAndRedirect();
    return null;
  }
};

// Force clean refresh token

// msalInstance.acquireTokenSilent = async () => {
//   const error = new InteractionRequiredAuthError("interaction_required", "Simulated refresh token expiry");
//   throw error;
// };

// Function to get user info
export const getUserInfo = (shouldCache: boolean = true) => {
  const account = msalInstance.getActiveAccount();
  if (!account) return null;

  const user = {
    fullName: account.name,
    email: account.username,
    localAccountId: account.localAccountId,
  };

  if (shouldCache) {
    localStorage.setItem("userEmail", user.email);
    localStorage.setItem("userObjectId", user.localAccountId);
  }

  return user;
};

export const getUserGroups = async (): Promise<Array<{ id: string; displayName: string }> | null> => {
  try {
    let account = msalInstance.getActiveAccount();

    if (!account) {
      const accounts = msalInstance.getAllAccounts();
      if (accounts.length > 0) {
        account = accounts[0];
        msalInstance.setActiveAccount(account);
      } else {
        console.warn("No active account found. Skipping group fetch.");
        return null;
      }
    }

    let tokenResponse;
    try {
      tokenResponse = await msalInstance.acquireTokenSilent({
        scopes: ["User.Read", "Group.Read.All"],
        account,
      });
    } catch (error: any) {
      if (error.errorCode === "interaction_required") {
        console.warn("Admin consent or interaction required for Group.Read.All. Skipping group fetch.");
        return null;
      } else {
        console.error("Error acquiring token for Graph API:", error);
        return null;
      }
    }

    const token = tokenResponse.accessToken;

    const response = await axios.get(
      "https://graph.microsoft.com/v1.0/me/memberOf?$select=id,displayName",
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    return response.data.value.map((group: { id: string; displayName: string }) => ({
      id: group.id,
      displayName: group.displayName,
    }));
  } catch (error) {
    console.error("Error fetching user groups:", error);
    return null;
  }
};

export const getGroupDisplayNames = async (): Promise<string[]> => {
  const cacheKey = "userGroupDisplayNames";

  const cached = localStorage.getItem(cacheKey);
  if (cached) {
    try {
      const parsed = JSON.parse(cached);
      if (Array.isArray(parsed) && parsed.every(name => typeof name === "string")) {
        console.log("Using cached group display names");
        return parsed;
      }
    } catch (e) {
      console.warn("Invalid group cache, ignoring.");
    }
  }

  const groups = await getUserGroups();
  if (!groups) {
    console.warn("No groups found or error occurred.");
    return [];
  }

  const displayNames = groups.map(group => group.displayName);
  localStorage.setItem(cacheKey, JSON.stringify(displayNames));
  return displayNames;
};

const checkToken = async () => {
  try {
    // console.log("Checking token after refresh...");
    const token = await getAuthToken();

    if (token) {
      // console.log("Token acquired successfully:", token);
      const groups = await getUserGroups();
      if (groups) {
        // console.log("User is a member of these groups:", groups);
      } else {
        console.warn("No groups found or an error occurred.");
      }
    } else {
      console.warn("No token acquired, user might need to log in again.");
    }
  } catch (error) {
    console.error("Failed to acquire token:", error);
  }
};

// export const getUserProfilePhoto = async (): Promise<string | null> => {
//   const cachedPhoto = localStorage.getItem("userProfilePhoto");

//   if (cachedPhoto) {
//     return cachedPhoto;
//   }

//   try {
//     let account = msalInstance.getActiveAccount();
//     if (!account) {
//       const accounts = msalInstance.getAllAccounts();
//       if (accounts.length > 0) {
//         account = accounts[0];
//         msalInstance.setActiveAccount(account);
//       } else {
//         console.warn("No account found.");
//         return null;
//       }
//     }

//     const tokenResponse = await msalInstance.acquireTokenSilent({
//       scopes: ["User.Read"],
//       account,
//     });

//     const photoResponse = await axios.get("https://graph.microsoft.com/v1.0/me/photo/$value", {
//       headers: {
//         Authorization: `Bearer ${tokenResponse.accessToken}`,
//       },
//       responseType: "blob",
//     });

//     // Convert blob to Base64
//     const base64Photo = await blobToBase64(photoResponse.data);
//     localStorage.setItem("userProfilePhoto", base64Photo);

//     return base64Photo;
//   } catch (error) {
//     console.error("Failed to fetch user profile photo:", error);
//     return null;
//   }
// };

// // Utility to convert Blob to Base64
// const blobToBase64 = (blob: Blob): Promise<string> => {
//   return new Promise((resolve, reject) => {
//     const reader = new FileReader();
//     reader.onloadend = () => {
//       if (reader.result && typeof reader.result === "string") {
//         resolve(reader.result);
//       } else {
//         reject("Failed to convert blob to base64");
//       }
//     };
//     reader.onerror = reject;
//     reader.readAsDataURL(blob);
//   });
// };


// Initialize MSAL once and check for a token
initializeMSAL()
  .then((isLoggedIn) => {
    if (isLoggedIn) {
      checkToken();
    } else {
      
    }
  })
  .catch((error) => {
    console.error("Initialization failed:", error);
  });

